import axios from "axios";
import { FilterTypes, NotImplementedError, UnauthenticatedError } from "../..";
import { PromiseHandler } from "../../handlers";
import { parseIdFilter } from "../../handlers/filter";
import { agentSessionFromService } from "../../models/aiAgents";
import { agentSlug } from "./agents.config";
import { AgentSessionCreationFailedError, AgentSessionNotFoundError, handleAgentSessionRequestError } from "./errors";
/**
 * AgentSessionIds is a class that manages ai agent session IDs.
 * When you call `find` (or `find_once`) {@link AgentSessionIds.findItems}, it will check if the session ID exists.
 * - If it does, it will return the session ID.
 * - If it doesn't exist:
 *   - and `ensuredSession` is true, it will create a new session ID and return it.
 *   - and `ensuredSession` is false, it will throw an error.
 * Calling `save` {@link AgentSessionIds.saveItem} will always create a new session ID.
 */
export class AgentSessionIds extends PromiseHandler {
    constructor(sdk, agent, { ensuredSession } = {}) {
        super(sdk, `ai.agents.${agent}.${ensuredSession ? "ensuredIds" : "ids"}`, { atomize: true });
        this.ensuredSession = ensuredSession ?? false;
        this.agentName = agent;
    }
    get agent() {
        return this.agentName;
    }
    /**
     * Finds session IDs based on the provided filters.
     * @throws {AgentSessionNotFoundError} If the session ID is not found and `ensuredSession` is false.
     * @throws {UnauthenticatedError} If the user is not authenticated.
     */
    async findItems(filters) {
        this.assertUser();
        const id = parseIdFilter(filters)?.values().next().value;
        // if no id or "new", we don't want to cache the result
        if (!id || id === "new") {
            if (!this.ensuredSession) {
                throw new AgentSessionNotFoundError(id || "undefined");
            }
            return this.createId();
        }
        return this.cache.promise(id, async () => {
            if (await this.sessionExists(id)) {
                return [{ id }];
            }
            if (!this.ensuredSession && !id.includes(":")) {
                throw new AgentSessionNotFoundError(id);
            }
            return this.createId();
        });
    }
    /**
     * Save is used to create a new session ID.
     * @returns The newly created session ID.
     * @throws {UnauthenticatedError} If the user is not authenticated.
     * @throws {AgentSessionCreationFailedError} If the session ID could not be created.
     */
    async saveItem() {
        this.assertUser();
        try {
            if (this.sdk.ai.isLegacyEndpoint(this.agent)) {
                return await this.createSessionLegacy();
            }
            // ADK has no dedicated "create session" REST verb — it's a sync `class_method`
            // dispatched through the same `:query` endpoint used for `get_session`.
            const { data } = await this.sdk.ai.withIdTokenRetry(this.agent, async () => axios.post(`${this.sdk.urls.baseAiAgentsUrl}/${agentSlug(this.agent)}/:query`, {
                class_method: "async_create_session",
                input: { user_id: this.sdk.getCurrentUser()?.id }
            }, await this.sdk.ai.axiosRequestConfig(this.agent)));
            const session = data?.output;
            if (!session?.id) {
                throw new AgentSessionCreationFailedError();
            }
            // Convert the raw ADK session to the canonical shape before caching so the list cache stays
            // uniformly camelCased, matching what the lists endpoint returns.
            this.sdk.ai.agents[this.agent].lists.reconcileCache("save", [
                agentSessionFromService(session)
            ]);
            return { id: session.id };
        }
        catch (err) {
            return handleAgentSessionRequestError(err, {
                defaultError: new AgentSessionCreationFailedError()
            });
        }
    }
    async createSessionLegacy() {
        const response = await axios.post(`${this.sdk.urls.baseAiAgentsUrlLegacy}/${this.agent}/sessions`, {}, await this.sdk.ai.axiosRequestConfig(this.agent));
        if (!response.data?.id) {
            throw new AgentSessionCreationFailedError();
        }
        // We want to clear the cache for the session lists whenever a new session ID is created to ensure that any subsequent calls to fetch the session lists will include the newly created session.
        this.sdk.ai.agents[this.agent].lists.reconcileCache("save", [response.data]);
        return { id: response.data.id };
    }
    /**
     * Checks if a session ID exists by calling the API.
     * @returns true if the session ID exists, false otherwise.
     */
    async sessionExists(id) {
        // Determine if the session ID is valid (non-empty and does not contain ":")
        // If ID includes ":", it is a proxy id and should not call the API to check for existence
        if (!id?.trim() || id.includes(":")) {
            return false;
        }
        try {
            const currentSession = await this.sdk.ai.agents[this.agent].find_once({
                where: [{ field: "id", value: id, type: FilterTypes.EQ }]
            });
            return !!currentSession?.id && currentSession.id === id;
        }
        catch (err) {
            if (err instanceof AgentSessionNotFoundError) {
                return false;
            }
            // If the error is not a 404, rethrow it
            throw err;
        }
    }
    async createId() {
        const sessionId = await this.save({});
        if (sessionId.id) {
            return [sessionId];
        }
        return [];
    }
    /**
     * Asserts that the user is authenticated.
     * @throws {UnauthenticatedError} If the user is not authenticated.
     */
    assertUser() {
        const currentAccount = this.sdk.authentication.getCurrentAccount();
        if (!currentAccount?.user?.id) {
            throw new UnauthenticatedError();
        }
    }
    /**
     * @deprecated Deleting agent sessions is not supported.
     */
    async deleteItem(_id) {
        throw new NotImplementedError("Deleting agent sessions is not supported.");
    }
}
//# sourceMappingURL=sessionIds.js.map